import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/integrations/supabase/client";

/** Where one document is filed, read from its own row. */
export interface DocumentFiling {
  projectId: number | null;
  folderId: number | null;
  /**
   * The project the FOLDER belongs to, which is not always the project the
   * document claims: `documents.project_id` and `documents.folder_id` are
   * written independently and nothing enforces that they agree. Five live
   * documents on develop (17625, 17646–17649) say project 264 while sitting in
   * folders 1891/1892 of project 262.
   *
   * Only meaningful when `folderRead` — the join is a left join, so an absent
   * or RLS-withheld folder collapses to `null` here, which is also what a real
   * building-root folder reads as.
   */
  folderProjectId: number | null;
  /**
   * Whether the folder row itself came back. `folderProjectId: null` alone
   * cannot say: a folder we could not read and a folder genuinely at the
   * building root are the same value. Documents at the building root are
   * `projectId: null` too, so without this the null-equals-null case adopts a
   * folder nobody vouched for — the failure this whole check exists to stop.
   */
  folderRead: boolean;
}

/**
 * Where a signing files its container.
 *
 * `agreed: false` is a refusal, not a fallback: a signing lives at the PROJECT
 * level, so documents from two different projects have no single place for the
 * container to land. Filing it at the building root instead would produce a
 * container that belongs to neither project and appears in the files list of
 * neither — which is the bug this type exists to make impossible.
 */
export type SigningTarget =
  | { agreed: true; projectId: number | null; folderId: number | null }
  | { agreed: false };

/**
 * The target is the documents' own filing, never the page the user happened to
 * be on. `/buildings/:id/documents` in Building mode lists every project of the
 * building at once with no project in scope, so page scope there is null while
 * the documents are filed in up to ten different projects.
 *
 * All-null projects is a legitimate answer, not a disagreement: 307 live
 * documents on develop sit at the building root, and signing them is allowed —
 * they just produce a container with no project either.
 *
 * The folder is adopted only on unanimous agreement, AND only when that folder
 * was read and belongs to the project being adopted. A partial agreement has no honest
 * answer (a container can be in one folder), and a folder from another project
 * would file the container somewhere it does not belong — a container reading
 * as project A on the Signing page while sitting in a project-B folder. The
 * project root is the one place that is true for every document in the set.
 */
export function deriveSigningTarget(filings: DocumentFiling[]): SigningTarget {
  if (filings.length === 0) return { agreed: true, projectId: null, folderId: null };

  const projects = new Set(filings.map((f) => f.projectId));
  if (projects.size !== 1) return { agreed: false };
  const projectId = filings[0].projectId;

  const folders = new Set(filings.map((f) => f.folderId));
  const unanimous = folders.size === 1 ? filings[0] : null;
  return {
    agreed: true,
    projectId,
    folderId:
      unanimous?.folderRead && unanimous.folderProjectId === projectId ? unanimous.folderId : null,
  };
}

/**
 * How many ids one filing read asks for.
 *
 * PostgREST truncates any response at `max_rows` — 1000 for this project
 * (`supabase/config.toml`) — silently, with no error and no marker. A truncated
 * page is indistinguishable here from rows RLS withheld, so signing 1000+
 * documents would fail the completeness guard below and dead-end on "Could not
 * prepare this signing" with a Try again that can never succeed. Batching well
 * under the cap makes every page whole. Raising `max_rows` is not the fix: the
 * cap is what stops a single request reading the whole table.
 */
const FILING_BATCH_SIZE = 500;

/** One page of the filing read. The completeness guard lives with the caller,
 *  which is the only place that knows how many rows the whole set should be. */
async function fetchFilingBatch(documentIds: number[]) {
  // The FK is named on purpose. There is one FK between these two tables today,
  // but an unnamed embed becomes ambiguous (PGRST201) the day a second one is
  // added — which has already blanked a whole feature here once. A left join, so
  // an unreadable or absent folder leaves the row itself intact.
  const { data, error } = await supabase
    .from("documents")
    .select(
      "id, project_id, folder_id, folder:document_folders!documents_folder_id_fkey(project_id)"
    )
    .in("id", documentIds);
  if (error) throw error;
  return data ?? [];
}

/**
 * Read the filing of every document being signed, straight from `documents`.
 *
 * Deliberately a fetch rather than a prop carried down from the lists: a row is
 * re-shaped six times between PostgREST and a rendered row, the signature panel
 * hands over a seventh shape of its own, and the bulk resolver recovers ids no
 * surface renders at all. A field threaded through all of that can silently
 * arrive `undefined`, and a document that has drifted from where it says it is
 * filed is precisely the failure being fixed. One read of the row is the only
 * source that cannot drift.
 */
async function fetchDocumentFilings(documentIds: number[]): Promise<DocumentFiling[]> {
  if (documentIds.length === 0) return [];

  const batches: number[][] = [];
  for (let i = 0; i < documentIds.length; i += FILING_BATCH_SIZE) {
    batches.push(documentIds.slice(i, i + FILING_BATCH_SIZE));
  }
  const rows = (await Promise.all(batches.map(fetchFilingBatch))).flat();

  // A row RLS withholds is a filing we cannot vouch for, and guessing it is how
  // a container lands in the wrong project. Refuse the whole signing instead.
  if (rows.length !== documentIds.length) {
    throw new Error(`Could not read the filing of all ${documentIds.length} documents`);
  }

  return rows.map((row) => ({
    projectId: row.project_id ?? null,
    folderId: row.folder_id ?? null,
    folderProjectId: row.folder?.project_id ?? null,
    // No folder row — none set, or one this user cannot read — drops the folder
    // in favour of the project root, whatever the documents' own project is.
    // Filing a container into a folder we cannot vouch for is the failure being
    // fixed, and building-root documents (project null) would otherwise adopt
    // an unreadable folder purely because null matches null.
    folderRead: row.folder != null,
  }));
}

/**
 * The signing target for a set of documents.
 *
 * `staleTime: 0` overrides the app-wide 5 minutes on purpose — a document moved
 * to another folder minutes ago must not be signed into the folder it left.
 *
 * It is not enough on its own: it only schedules a refetch, so reopening the
 * dialog within the app-wide 5-minute `gcTime` hands back the PREVIOUS filing
 * synchronously, `isPending` is false, and the first paint offers a Sign button
 * wired to where the documents used to be. `gcTime: 0` drops the entry the
 * moment the dialog unmounts; the LOAD-BEARING half of that guarantee, though,
 * is the caller's `isFetching` gate — see `preflight` in SignDocumentsDialog.
 * A refetch over data that is already cached is the same lie in miniature, and
 * `isFetching` is what has no form during one.
 */
export function useSigningTarget(documentIds: number[]) {
  const ids = [...new Set(documentIds)].sort((a, b) => a - b);
  return useQuery({
    queryKey: ["signing-target-filing", ids],
    queryFn: () => fetchDocumentFilings(ids),
    staleTime: 0,
    gcTime: 0,
    // The one refetch trigger that is never worth it here. The query is always
    // stale, so React Query's default would re-run this on every return to the
    // tab — and because the caller has no form while a read is in flight, an
    // alt-tab mid-form swaps the form for the checking panel and takes the
    // caret out of the container-name field. The freshness this read exists to
    // protect is about REOPENING the dialog, which the fresh mount (gcTime: 0)
    // and the `isFetching` gate already cover.
    refetchOnWindowFocus: false,
    // The cache entry stays the raw filings; every consumer derives its own view.
    select: deriveSigningTarget,
  });
}
